/**
 * Matching a re-imported file's chapters to the book's (decision 64; part 23a, table MA): by the chapter IDs the export
 * put in each heading's bookmark, falling back to the heading's text and then to its place, so a renamed heading
 * doesn't make a duplicate. A chapter of the book the file doesn't match is listed, never deleted. Pure.
 */

/** A file chapter as matching reads it: the chapter ID its heading carries (null: none) and its title. */
export interface FileChapter {
  readonly chapter: string | null;
  readonly title: string | null;
}

/** A live chapter of the book, in the book's order. */
export interface BookChapter {
  readonly id: string;
  readonly title: string;
}

/** What a file chapter is in the book: a chapter it matched, or a new chapter after its file predecessor (null: first). */
export type ChapterMatch = { readonly kind: 'matched'; readonly id: string; readonly by: 'id' | 'title' | 'place' } | { readonly kind: 'new'; readonly after: number | null };

/** Table MA's outcome: each file chapter's match, in the file's order, and the book's chapters none matched (MA7). */
export interface Matching {
  readonly matches: readonly ChapterMatch[];
  readonly missing: readonly string[];
}

/**
 * MA4: a title as matching compares it: quotes and apostrophes of any kind one mark with no white space beside it (the
 * export sets French guillemets with narrow no-break spaces inside, which a heading's text reads back as spaces: deep-fix
 * r10), white space collapsed.
 */
export function titleKey(title: string): string {
  return title
    .replaceAll(/["'`\u2018-\u201f\u2032\u2033\u00ab\u00bb\u2039\u203a]/gu, "'")
    .replaceAll(/\s*'\s*/gu, "'")
    .replaceAll(/\s+/gu, ' ')
    .trim();
}

/** Table MA: `file`'s chapters matched to `book`'s live chapters. */
export function matchChapters(file: readonly FileChapter[], book: readonly BookChapter[]): Matching {
  // Each live chapter by its ID, and its place; asked for no ID (a file chapter of none, a neighbour past either end), none.
  const liveOf = new Map<string | null, BookChapter>(book.map((chapter) => [chapter.id, chapter]));
  const placeOf = new Map<string | undefined, number>(book.map((chapter, i) => [chapter.id, i]));
  /** Each file chapter's match so far: the book chapter's ID, and how. */
  const matched: ({ readonly id: string; readonly by: 'id' | 'title' | 'place' } | null)[] = file.map(() => null);
  const taken = new Set<string>();
  // MA1–MA3: each ID once, the first file chapter carrying it.
  file.forEach((chapter, i) => {
    const live = liveOf.get(chapter.chapter);
    if (live === undefined || taken.has(live.id)) return;
    taken.add(live.id);
    matched[i] = { id: live.id, by: 'id' };
  });
  // MA4: a title one unmatched book chapter and one unmatched file chapter share.
  const keyOf = (title: string | null): string => (title === null ? '' : titleKey(title));
  const count = <T,>(items: readonly T[], key: (item: T) => string): Map<string, number> => {
    const counts = new Map<string, number>();
    for (const item of items) counts.set(key(item), (counts.get(key(item)) ?? 0) + 1);
    return counts;
  };
  const open = book.filter((chapter) => !taken.has(chapter.id));
  const bookTitles = count(open, (chapter) => keyOf(chapter.title));
  /** The unmatched book chapter of each title only one has. */
  const only = new Map(open.filter((chapter) => bookTitles.get(keyOf(chapter.title)) === 1).map((chapter) => [keyOf(chapter.title), chapter]));
  const fileTitles = count(
    file.filter((_, i) => matched[i] === null),
    (chapter) => keyOf(chapter.title),
  );
  file.forEach((chapter, i) => {
    const key = keyOf(chapter.title);
    const target = only.get(key);
    if (matched[i] !== null || key === '' || target === undefined || fileTitles.get(key) !== 1) return;
    taken.add(target.id);
    matched[i] = { id: target.id, by: 'title' };
  });
  // MA5: an unmatched file chapter whose neighbours are matched (or an end of the file) is the one unmatched book
  // chapter between theirs, if there is exactly one.
  const snapshot = [...matched];
  file.forEach((_, i) => {
    if (snapshot[i] !== null || snapshot[i - 1] === null || snapshot[i + 1] === null) return;
    const low = placeOf.get(snapshot[i - 1]?.id) ?? -1;
    const high = placeOf.get(snapshot[i + 1]?.id) ?? book.length;
    const [one, other] = book.slice(low + 1, high).filter((chapter) => !taken.has(chapter.id));
    if (one === undefined || other !== undefined) return;
    taken.add(one.id);
    matched[i] = { id: one.id, by: 'place' };
  });
  // MA6: the rest new, each after its file predecessor.
  const matches = matched.map((match, i): ChapterMatch => (match === null ? { kind: 'new', after: i === 0 ? null : i - 1 } : { kind: 'matched', id: match.id, by: match.by }));
  return { matches, missing: book.filter((chapter) => !taken.has(chapter.id)).map((chapter) => chapter.id) };
}
